import {
  EEventContextAction,
  EEventType,
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { getHead } from '../../head';
import LinearProgress from '../components/linear-progress';
import Photos from '../components/photos';
import Breadcrumbs from '../components/product/breadcrumbs';
import Description from '../components/product/description';
import FeatureFlags from '../components/product/feature-flags';
import LanguageMutationLinks from '../components/product/language-mutation-links';
import Offer from '../components/product/offer';
import ProductReviews from '../components/product/product-reviews';
import ProductSafety from '../components/product/product-safety';
import Properties from '../components/product/properties';
import Related from '../components/product/related';
import AggregateStars from '../components/product/stars';
import { eventSend } from '../reducers/event/reducer';
import { type ILanguageState } from '../reducers/language/types';
import { type IProductState } from '../reducers/product/types';
import { watchdogReset } from '../reducers/product-watchdog/reducer';
import { setActiveCategory } from '../reducers/products/reducer';
import { type IUserState } from '../reducers/user/types';
function ProductContainer({
  Product,
  dispatch,
  Language,
  User,
}: {
  Product: IProductState;
  dispatch: Dispatch;
  Language: ILanguageState;
  User: IUserState;
}): React.JSX.Element {
  const { t } = useTranslation();

  useEffect((): (() => void) => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_PRODUCT,
        action: EEventContextAction.ENTER,
        context: { product: { uuid: Product.product?.uuid } },
      }),
    );

    return (): void => {
      dispatch(watchdogReset());
      dispatch(
        eventSend({
          type: EEventType.PAGE_PRODUCT,
          action: EEventContextAction.LEAVE,
          context: { product: { uuid: Product.product?.uuid } },
        }),
      );
    };
  }, [dispatch, Product.product?.uuid]);

  useEffect((): void => {
    if (!Product.product) {
      return;
    }

    const [category] = Product.product.categories;

    if (!category) {
      return;
    }

    dispatch(
      setActiveCategory({
        category: {
          uuid: category.uuid,
        },
      }),
    );
  }, [Product.product, Product.product?.categories, dispatch]);

  useEffect((): void => {
    if (!Product.product) {
      return;
    }

    const productNameEn: ITranslation | undefined = Product.product.names.find(
      (n: ITranslation): boolean => n.code === 'EN',
    );
    const productPriceEur: ICurrencyPrice | undefined =
      Product.product.prices.find(
        (c: ICurrencyPrice): boolean => c.isoCode === 'EUR',
      );
    const priceWithTax: ICountryCurrencyPrice | undefined =
      productPriceEur?.pricesWithTax.find(
        (c: ICountryCurrencyPrice): boolean =>
          c.isoCode === (User.countryType?.isoCode || 'CZE'),
      );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        items: [
          {
            item_id: Product.product.uuid,
            item_name: productNameEn?.value || '---',
            price: priceWithTax?.price || 0,
            currency: 'EUR',
          },
        ],
      },
    });
  }, [Product.product?.names, Product.product?.prices, Product.product?.uuid]);

  if (Product.isLoading) {
    return <LinearProgress />;
  }

  const {
    languageType: { code: languageCode },
  } = Language;

  let name: ITranslation | undefined = Product.product?.names.find(
    (n: ITranslation): boolean => n.code === languageCode,
  );

  if (!name) {
    name = Product.product?.names.find(
      (n: ITranslation): boolean => n.code === 'EN',
    );
  }

  if (typeof document !== 'undefined' && name) {
    const { title } = getHead(t, name ? name.value : '');
    document.title = title;
  }

  const headerTwoElements: string[] = [];

  if (Product.product?.brand && Product.product?.brand.name) {
    headerTwoElements.push(Product.product?.brand.name);
  }

  if (Product.product?.mpn) {
    headerTwoElements.push(Product.product?.mpn);
  }

  if (Product.product?.gtin) {
    headerTwoElements.push(Product.product?.gtin);
  }

  const headerTwo: string = headerTwoElements.join(' | ');

  if (!Product.product) {
    return (
      <p role="status">
        {t('product.notFound', { defaultValue: 'Product not found.' })}
      </p>
    );
  }

  return (
    <>
      <div className="product container">
        <Breadcrumbs />
        <h1>{name ? name.value : ''}</h1>
        {headerTwo && <h2>{headerTwo}</h2>}
      </div>

      <FeatureFlags
        featureFlags={Product.product.featureFlags}
        stock={Product.product.stock}
      />

      <Photos
        photos={Product.product.photos}
        name={name ? name.value : ''}
        dispatch={dispatch}
      />

      <Offer />

      <div className="product-layout--desc-props">
        <Description />
        <Properties />
      </div>

      <ProductSafety />

      <Related />

      {Product.product.reviews && Product.product.reviews.length > 0 && (
        <details className="product-reviews--collapsible">
          <summary className="product-reviews--summary">
            <span className="product-reviews--toggle-btn">
              <span className="product-reviews--summary--title">
                {t('product.reviews')}
                <span className="product-reviews--summary--count">
                  ({Product.product.reviews.length})
                </span>
              </span>
              {Product.product.averageRating != null &&
                Product.product.reviewCount != null && (
                  <AggregateStars
                    rating={Product.product.averageRating}
                    count={Product.product.reviewCount}
                  />
                )}
            </span>
          </summary>
          <ProductReviews reviews={Product.product.reviews} />
        </details>
      )}

      <LanguageMutationLinks />
    </>
  );
}

const mapStateToProps = ({ Language, User, Product }) => ({
  Language,
  User,
  Product,
});

export default connect(mapStateToProps)(ProductContainer);
